/**
 * Reparaciones.
 *
 * QUE PREGUNTA CONTESTA ESTA PANTALLA
 * -----------------------------------
 * "¿Dónde está el equipo de Martínez?" — mucho más seguido que "¿qué hay en el
 * taller?". Se pregunta por un equipo tres veces más de lo que se ingresa uno,
 * casi siempre con alguien esperando del otro lado del teléfono, así que lo
 * primero que se ve es el buscador.
 *
 * EL FLUJO REAL
 * -------------
 * Recibir, cargar el costo y el presupuesto cuando el técnico contesta, entregar y cobrar.
 * La lista se filtra por eso mismo: a cuáles les falta el número del técnico,
 * cuáles ya se pueden entregar, cuáles ya se fueron. Los nueve estados de la
 * base siguen ahí, validados, pero acá no se recorren uno por uno.
 *
 * UNA SOLA CONSULTA, TODO EL FILTRADO ACA
 * ---------------------------------------
 * Se traen las últimas 200 órdenes y el buscador, el filtro y la ficha salen
 * de esa misma lista. Con dos a cinco equipos por día, 200 órdenes son meses
 * de taller.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Plus, Wrench } from "lucide-react";

import { PageHeader } from "@/components/layout/PageHeader";
import { Pagina } from "@/components/layout/Pagina";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Segmented, type OpcionSegmented } from "@/components/ui/segmented";
import { BuscadorOrdenes } from "@/components/taller/BuscadorOrdenes";
import { DialogCostos } from "@/components/taller/DialogCostos";
import { DialogDevolverSena } from "@/components/taller/DialogDevolverSena";
import { DialogEntregar } from "@/components/taller/DialogEntregar";
import { DialogEstado } from "@/components/taller/DialogEstado";
import { DialogLiquidarTecnico } from "@/components/taller/DialogLiquidarTecnico";
import { DialogOrden, DialogOrdenNoEncontrada } from "@/components/taller/DialogOrden";
import { DialogRecibir } from "@/components/taller/DialogRecibir";
import { DialogPagarTecnico } from "@/components/taller/DialogPagarTecnico";
import { DialogPresupuesto } from "@/components/taller/DialogPresupuesto";
import { DialogSena } from "@/components/taller/DialogSena";
import { DialogTalon } from "@/components/taller/DialogTalon";
import { DialogVolvioAlStock } from "@/components/taller/DialogVolvioAlStock";
import { ListaOrdenes } from "@/components/taller/ListaOrdenes";
import { PieDeLista } from "@/components/ui/pie-de-lista";
import { SinRetirar } from "@/components/taller/SinRetirar";
import {
  ETAPAS,
  ETIQUETA_ESTADO,
  buscarOrdenes,
  conCostosCargados,
  contarPorEtapa,
  deudaTecnico,
  efectoDe,
  esInterna,
  filtrarPorEtapa,
  historialDelEquipo,
  indexarEnTaller,
  nombreOrden,
  sinRetirar,
  textoDeudaTecnico,
  type Etapa,
  type OrdenTaller,
} from "@/components/taller/logica";
import { monedaAnclaje } from "@/components/clientes/logica";
import {
  useCambiarEstadoOt,
  useCargarCostosOt,
  useCliente,
  useClientes,
  useConfig,
  useCrearCliente,
  useCuentasFinancieras,
  useDevolverSenaOt,
  useEntregarOt,
  useProducto,
  useProductos,
  useRecibirEquipo,
  useReparaciones,
  usePagarTecnicoOt,
  usePagarTecnicoLote,
  useSenaOt,
  useSugerenciasOt,
  useUsadosEnTaller,
} from "@/lib/queries";
import { mostrarError, mostrarExito } from "@/lib/errores";
import { useVerMas } from "@/lib/usar-ver-mas";
import { formatear } from "@tora/shared";
import { aNumero, type EntregaOt, type EstadoOt } from "@/lib/tipos";

export const Route = createFileRoute("/reparaciones")({
  component: Reparaciones,
  // El buscador global enlaza a una orden puntual con `?ot=`. Se valida en vez
  // de confiar: la URL la puede tipear cualquiera.
  validateSearch: (
    busqueda: Record<string, unknown>,
  ): { ot?: number; recibir?: number; liquidar?: boolean } => {
    const ot = Number(busqueda.ot);
    const recibir = Number(busqueda.recibir);
    return {
      ...(Number.isInteger(ot) && ot > 0 ? { ot } : {}),
      ...(Number.isInteger(recibir) && recibir > 0 ? { recibir } : {}),
      // Lo manda la fila del tecnico en /proveedores: la plata que se le debe
      // se ve alla, pero se liquida con el dialogo que vive aca.
      ...(busqueda.liquidar === "1" || busqueda.liquidar === true ? { liquidar: true } : {}),
    };
  },
});

/**
 * Cuantas ordenes y cuantos clientes se traen por pagina.
 *
 * La busqueda por texto y las etapas se resuelven en el navegador sobre lo que
 * entro en esta ventana, asi que el pie de la lista avisa cuando quedo algo
 * afuera. Moverlas a la base esta anotado en `docs/pendientes.md`.
 */
const POR_PAGINA = 200;

function Reparaciones(): JSX.Element {
  const { ot, recibir: recibirParam, liquidar: liquidarParam } = Route.useSearch();
  const navegar = useNavigate({ from: Route.fullPath });

  const [texto, setTexto] = useState("");
  const [etapa, setEtapa] = useState<Etapa | null>(null);
  const [abrirRecibir, setAbrirRecibir] = useState(false);
  const [productoInicial, setProductoInicial] = useState<number | undefined>(undefined);
  const [destino, setDestino] = useState<EstadoOt | null>(null);
  const [cargandoCostos, setCargandoCostos] = useState(false);
  // La orden a la que se le esta pasando el presupuesto. Es una copia y no un
  // booleano porque se abre APENAS se guardan los costos, y en ese instante la
  // lista todavia esta volviendo del servidor: el mensaje se arma con los
  // numeros que acaba de devolver la mutacion, no con los que quedaron en
  // pantalla. Desde el menu de la ficha se le pasa la orden tal cual.
  const [presupuesto, setPresupuesto] = useState<OrdenTaller | null>(null);
  const [dejandoSena, setDejandoSena] = useState(false);
  const [pagandoTecnico, setPagandoTecnico] = useState(false);
  const [devolviendoSena, setDevolviendoSena] = useState(false);
  const [liquidando, setLiquidando] = useState(liquidarParam === true);
  const [imprimiendo, setImprimiendo] = useState(false);
  const [entregando, setEntregando] = useState(false);

  const { limite, verMas, reiniciar } = useVerMas(POR_PAGINA);
  const { data: pagina, isFetching } = useReparaciones({ limite });
  const { data: clientes } = useClientes({ limite: POR_PAGINA });
  const { data: config } = useConfig();
  const { data: cuentasPagina } = useCuentasFinancieras();
  const { data: sugerencias } = useSugerenciasOt();
  // Los usados, para la casilla «Es un equipo nuestro», y cuales ya estan en el
  // tecnico. Se traen aca y no adentro del dialogo, que asi se prueba solo.
  const { data: usadosPagina } = useProductos({ es_usado: true, limite: POR_PAGINA });
  const usados = useMemo(() => usadosPagina?.filas ?? [], [usadosPagina]);
  const { data: enTallerPagina } = useUsadosEnTaller();
  const enTaller = useMemo(() => indexarEnTaller(enTallerPagina?.filas ?? []), [enTallerPagina]);

  const ordenes = useMemo<OrdenTaller[]>(() => pagina?.filas ?? [], [pagina]);
  const cotizacion = aNumero(config?.cotizacion_vigente);
  // Al tecnico se le paga en pesos, y al dolar de referencia SIN el ajuste fijo
  // del mostrador: ese ajuste es el margen con el que el local vende dolares, y
  // pagarle al tecnico no es venderle nada. La base usa el mismo numero por
  // defecto (`cotizacion_tecnico()`, migracion 0054); esto es para que la
  // pantalla muestre de antemano lo que va a salir.
  const cotizacionTecnico = aNumero(config?.cotizacion_tecnico) || cotizacion;
  const cuentas = useMemo(() => cuentasPagina?.filas ?? [], [cuentasPagina]);
  const regla = useMemo(
    () => ({
      ganancia: aNumero(config?.config?.reparacion_ganancia_usd ?? 40),
      diagnostico: aNumero(config?.config?.reparacion_diagnostico_usd ?? 20),
      anticipoPct: aNumero(config?.config?.reparacion_anticipo_pct ?? 60),
    }),
    [config],
  );

  const visibles = useMemo(
    () => filtrarPorEtapa(buscarOrdenes(ordenes, texto), etapa),
    [ordenes, texto, etapa],
  );
  // La ventana vuelve a la primera pagina al cambiar el filtro, como en el
  // resto de los listados: quedarse en la pagina 3 del filtro anterior muestra
  // un recorte que nadie pidio.
  useEffect(() => reiniciar(), [texto, etapa, reiniciar]);
  const porEtapa = useMemo(() => contarPorEtapa(ordenes), [ordenes]);

  // Los equipos sin retirar se calculan con la misma regla que `v_ot_sin_retirar`
  // sobre la lista que ya esta en pantalla.
  const olvidados = useMemo(() => sinRetirar(ordenes), [ordenes]);

  const orden = useMemo(() => ordenes.find((o) => o.id === ot) ?? null, [ordenes, ot]);
  const historial = useMemo(
    () => (orden ? historialDelEquipo(ordenes, orden) : []),
    [ordenes, orden],
  );
  // La ficha del usado de una orden interna: el cierre necesita su costo y su precio.
  const { data: productoDeLaOrden } = useProducto(orden?.producto_id ?? undefined);
  // El saldo a favor del cliente, para ofrecerlo al entregar como en la venta.
  // Solo mientras el dialogo esta abierto: la lista no lo necesita.
  const { data: fichaCliente } = useCliente(
    entregando && orden?.cliente_id ? orden.cliente_id : undefined,
  );
  const deuda = useMemo(() => textoDeudaTecnico(deudaTecnico(ordenes)), [ordenes]);

  const telefonoDe = useMemo(() => {
    const porId = new Map((clientes?.filas ?? []).map((c) => [c.id, c.telefono]));
    return (clienteId: number | null): string | null =>
      clienteId === null ? null : (porId.get(clienteId) ?? null);
  }, [clientes]);

  const recibir = useRecibirEquipo();
  const crearCliente = useCrearCliente();
  const cambiarEstado = useCambiarEstadoOt();
  const cargarCostos = useCargarCostosOt();
  const sena = useSenaOt();
  const devolverSena = useDevolverSenaOt();
  const pagarTecnico = usePagarTecnicoOt();
  const liquidarTecnico = usePagarTecnicoLote();
  const entregar = useEntregarOt();

  // `/reparaciones?recibir=123` es el atajo desde la permuta: abre el alta con
  // ese usado elegido. Se espera a tener la lista de usados para que el
  // dialogo lo encuentre, y el parametro se saca de la URL apenas se usa. El
  // ref evita reabrir el dialogo una segunda vez para el mismo id: si el
  // usuario ya lo cerro a mano mientras se esperaba a los usados, no vuelve.
  const recibirConsumido = useRef<number | null>(null);
  useEffect(() => {
    if (recibirParam === undefined || recibirConsumido.current === recibirParam) return;
    if (!usadosPagina) return;
    recibirConsumido.current = recibirParam;
    setProductoInicial(recibirParam);
    setAbrirRecibir(true);
    void navegar({ search: {}, replace: true });
  }, [recibirParam, usadosPagina, navegar]);

  function abrirOrden(id: number): void {
    void navegar({ search: { ot: id } });
  }
  function volverALaLista(): void {
    void navegar({ search: {} });
  }

  function confirmarEntrega(entrega: EntregaOt): void {
    if (!orden) return;
    entregar.mutate(
      { id: orden.id, entrega },
      {
        onSuccess: (r) => {
          setEntregando(false);
          if (r.producto_id) {
            mostrarExito(
              `${nombreOrden(orden)} · volvió al stock`,
              r.cierre === "SIN_ARREGLO"
                ? "Sin arreglo: el costo del equipo no cambió."
                : `El costo del equipo pasó a ${formatear(aNumero(r.costo_nuevo), r.costo_moneda ?? "ARS")}.${r.ya_vendido ? " Ojo: ya se había vendido." : ""}`,
            );
            return;
          }
          const pendiente = aNumero(r.pendiente);
          // El vuelto se dice en el cartel: son los pesos que el cajero da.
          const vuelto = aNumero(r.vuelto ?? 0);
          mostrarExito(
            r.comprobante
              ? `${nombreOrden(orden)} entregada · ${r.comprobante}`
              : `${nombreOrden(orden)} entregada`,
            pendiente > 0.009
              ? `Quedan ${formatear(pendiente, r.moneda)} en la cuenta corriente del cliente.`
              : vuelto > 0.009
                ? `Cobrada entera. Vuelto: ${formatear(vuelto, "ARS")}. Los accesos del equipo se borraron.`
                : r.comprobante
                  ? "Cobrada entera. Los accesos del equipo se borraron."
                  : "Sin cobrar. Los accesos del equipo se borraron.",
          );
        },
        onError: mostrarError,
      },
    );
  }

  // Con un dialogo de accion abierto (cargar costos, entregar, pagar), el de la
  // orden se aparta: dos ventanas superpuestas serian dos velos negros y dos
  // Escape. Cuando la accion termina, la orden vuelve sola y con el dato nuevo.
  const haySubdialogo =
    cargandoCostos ||
    presupuesto !== null ||
    dejandoSena ||
    pagandoTecnico ||
    devolviendoSena ||
    imprimiendo ||
    entregando ||
    destino !== null;
  const hayFiltros = texto.trim() !== "" || etapa !== null;
  function limpiarFiltros(): void {
    setTexto("");
    setEtapa(null);
  }

  return (
    <Pagina>
      <PageHeader
        titulo="Reparaciones"
        icono={Wrench}
        acciones={
          <Button type="button" onClick={() => setAbrirRecibir(true)}>
            <Plus aria-hidden="true" />
            Recibir equipo
          </Button>
        }
      />

      {/* La deuda con el tecnico se salda entera de una (lo normal: al
          tercerizado se le paga cada tanto por todo lo junto) o orden por
          orden, y la segunda accion lleva al chip que las reune. */}
      {deuda ? (
        <Aviso
          tono="warning"
          accion={
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" size="sm" onClick={() => setLiquidando(true)}>
                Pagarle todo
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setTexto("");
                  setEtapa("FALTA_PAGAR_TECNICO");
                }}
              >
                Ver esas órdenes
              </Button>
            </div>
          }
        >
          {deuda}. Se salda todo junto acá, o desde cada orden con «Pagar al técnico».
        </Aviso>
      ) : null}

      {/* El buscador y las etapas en una fila: es lo que se mira antes de
          cualquier otra cosa, y no hace falta que ocupe dos. Con un piso de
          ancho: a 1280 px las etapas lo dejaban en 186 px y el placeholder se
          leia «Apellido, IMEI c». */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start">
        <div className="min-w-0 flex-1 lg:min-w-72">
          <BuscadorOrdenes
            texto={texto}
            onTexto={setTexto}
            encontradas={visibles.length}
            total={ordenes.length}
          />
        </div>
        <FiltroEtapas
          etapa={etapa}
          onEtapa={setEtapa}
          cantidades={porEtapa}
          total={ordenes.length}
        />
      </div>

      <SinRetirar ordenes={olvidados} telefonoDe={telefonoDe} onAbrir={abrirOrden} />

      {/*
        La lista ocupa todo el ancho, siempre. La orden abierta va en un
        dialogo encima (ver `DialogOrden`): asi la lista no tiene que ceder la
        mitad de la pantalla ni desaparecer en el celular.
      */}
      <ListaOrdenes
        ordenes={visibles}
        seleccionadaId={ot ?? null}
        onSeleccionar={abrirOrden}
        cargando={isFetching}
        consulta={texto}
        total={ordenes.length}
        onRecibir={() => setAbrirRecibir(true)}
        onLimpiarFiltros={hayFiltros ? limpiarFiltros : undefined}
        pie={
          <PieDeLista
            visibles={ordenes.length}
            total={pagina?.total ?? 0}
            sustantivo="órdenes"
            onVerMas={verMas}
            cargando={isFetching}
            detalle="Es el máximo que se trae de una, y el buscador y las etapas de arriba trabajan sobre estas."
          />
        }
      />

      {orden && !haySubdialogo ? (
        <DialogOrden
          orden={orden}
          telefono={telefonoDe(orden.cliente_id ?? null)}
          anticipoPct={regla.anticipoPct}
          onCerrar={volverALaLista}
          onCargarCostos={() => setCargandoCostos(true)}
          onPresupuesto={() => setPresupuesto(orden)}
          onSena={() => setDejandoSena(true)}
          onPagarTecnico={() => setPagandoTecnico(true)}
          onDevolverSena={() => setDevolviendoSena(true)}
          onImprimirTalon={() => setImprimiendo(true)}
          onEntregar={() => setEntregando(true)}
          onCambiarEstado={setDestino}
          historial={historial}
          onAbrirOrden={abrirOrden}
        />
      ) : null}

      {/* `?ot=` que no existe. Se espera a que la lista llegue: mientras carga,
          NINGUNA orden esta en `ordenes` y el aviso seria mentira. */}
      {ot !== undefined && orden === null && pagina !== undefined ? (
        <DialogOrdenNoEncontrada tope={limite} onCerrar={volverALaLista} />
      ) : null}

      <DialogRecibir
        abierto={abrirRecibir}
        onAbiertoChange={(abierto) => {
          setAbrirRecibir(abierto);
          if (!abierto) {
            setProductoInicial(undefined);
            recibirConsumido.current = recibirParam ?? recibirConsumido.current;
          }
        }}
        clientes={clientes?.filas ?? []}
        cuentas={cuentas}
        cotizacion={cotizacion}
        diagnostico={regla.diagnostico}
        guardando={recibir.isPending}
        usados={usados}
        enTaller={enTaller}
        productoInicial={productoInicial}
        // El talon pide "titular" y "numero de contacto": si la persona no esta
        // cargada, con esos dos datos se la crea desde aca, sin ir a Clientes.
        onCrearCliente={(datos) => crearCliente.mutateAsync(datos)}
        sugerencias={sugerencias}
        onConfirmar={(datos) =>
          recibir.mutate(datos, {
            onSuccess: (nueva) => {
              setAbrirRecibir(false);
              setProductoInicial(undefined);
              mostrarExito(
                `Entró ${datos.equipo}`,
                datos.producto_id
                  ? `${nueva.numero} · equipo del stock, sin nada que cobrar`
                  : nueva.cobro
                    ? `${nueva.numero} · cobrado ${formatear(aNumero(nueva.cobro.importe), nueva.cobro.moneda)} de diagnóstico`
                    : `${nueva.numero} · queda debiendo el diagnóstico`,
              );
              abrirOrden(nueva.orden_id);
            },
            onError: mostrarError,
          })
        }
      />

      <DialogCostos
        orden={cargandoCostos ? orden : null}
        regla={regla}
        cotizacion={cotizacion}
        guardando={cargarCostos.isPending}
        onCerrar={() => setCargandoCostos(false)}
        onConfirmar={(costos) => {
          if (!orden) return;
          cargarCostos.mutate(
            { id: orden.id, costos },
            {
              onSuccess: (r) => {
                setCargandoCostos(false);
                mostrarExito(
                  esInterna(orden)
                    ? `${nombreOrden(orden)} · suma ${formatear(aNumero(r.precio_cliente), r.moneda)} al costo`
                    : `${nombreOrden(orden)} · precio ${formatear(aNumero(r.precio_cliente), r.moneda)}`,
                  esInterna(orden)
                    ? "Cuando vuelva, se cierra la orden y el costo pasa al equipo."
                    : "Pasale el presupuesto al cliente; cuando lo apruebe, se entrega y se cobra.",
                );
                // El equipo propio no tiene a quien avisarle, y sin telefono el
                // chat no se abre. En los dos casos el paso se saltea: el item
                // «Pasar el presupuesto» de la ficha sigue ahi para copiarlo.
                if (esInterna(orden)) return;
                if (!telefonoDe(orden.cliente_id ?? null)) return;
                setPresupuesto(conCostosCargados(orden, r, costos.diagnostico));
              },
              onError: mostrarError,
            },
          );
        }}
      />

      <DialogPresupuesto
        orden={presupuesto}
        telefono={presupuesto ? telefonoDe(presupuesto.cliente_id ?? null) : null}
        anticipoPct={regla.anticipoPct}
        onCerrar={() => setPresupuesto(null)}
      />

      <DialogTalon
        orden={imprimiendo ? orden : null}
        empresa={config?.config ?? null}
        telefono={orden ? telefonoDe(orden.cliente_id ?? null) : null}
        onCerrar={() => setImprimiendo(false)}
      />

      <DialogPagarTecnico
        orden={pagandoTecnico ? orden : null}
        cuentas={cuentas}
        cotizacion={cotizacionTecnico}
        guardando={pagarTecnico.isPending}
        onCerrar={() => setPagandoTecnico(false)}
        onConfirmar={(pago) => {
          if (!orden) return;
          pagarTecnico.mutate(
            { id: orden.id, pago },
            {
              onSuccess: (r) => {
                setPagandoTecnico(false);
                mostrarExito(
                  `Pagaste ${formatear(aNumero(r.importe), r.moneda)} de ${r.cuenta}`,
                  `${nombreOrden(orden)} · el costo del técnico ya es un egreso de la caja.`,
                );
              },
              onError: mostrarError,
            },
          );
        }}
      />

      <DialogLiquidarTecnico
        abierto={liquidando}
        ordenes={ordenes}
        cuentas={cuentas}
        cotizacion={cotizacionTecnico}
        guardando={liquidarTecnico.isPending}
        onCerrar={() => setLiquidando(false)}
        onConfirmar={(datos) =>
          liquidarTecnico.mutate(datos, {
            onSuccess: (r) => {
              setLiquidando(false);
              const salio = (["USD", "ARS"] as const)
                .filter((m) => aNumero(r.total[m]) > 0.009)
                .map((m) => formatear(aNumero(r.total[m]), m))
                .join(" y ");
              mostrarExito(
                `Le pagaste ${salio} al técnico`,
                `${r.ordenes === 1 ? "1 orden saldada" : `${r.ordenes} órdenes saldadas`} · ${r.movimientos === 1 ? "1 egreso" : `${r.movimientos} egresos`} en la caja.`,
              );
            },
            onError: mostrarError,
          })
        }
      />

      <DialogSena
        orden={dejandoSena ? orden : null}
        cuentas={cuentas}
        cotizacion={cotizacion}
        anticipoPct={regla.anticipoPct}
        guardando={sena.isPending}
        onCerrar={() => setDejandoSena(false)}
        onConfirmar={(pago) => {
          if (!orden) return;
          sena.mutate(
            { id: orden.id, pago },
            {
              onSuccess: (r) => {
                setDejandoSena(false);
                mostrarExito(
                  `Seña de ${formatear(aNumero(r.importe), r.moneda)} en ${r.cuenta}`,
                  `${nombreOrden(orden)} · se descuenta al entregar.`,
                );
              },
              onError: mostrarError,
            },
          );
        }}
      />

      <DialogDevolverSena
        orden={devolviendoSena ? orden : null}
        cuentas={cuentas}
        guardando={devolverSena.isPending}
        onCerrar={() => setDevolviendoSena(false)}
        onConfirmar={(pago) => {
          if (!orden) return;
          devolverSena.mutate(
            { id: orden.id, pago },
            {
              onSuccess: (r) => {
                setDevolviendoSena(false);
                mostrarExito(
                  `Devueltos ${formatear(aNumero(r.importe), r.moneda)} de ${r.cuenta}`,
                  `${nombreOrden(orden)} · anulada.`,
                );
              },
              onError: mostrarError,
            },
          );
        }}
      />

      {orden && esInterna(orden) ? (
        <DialogVolvioAlStock
          orden={entregando ? orden : null}
          producto={productoDeLaOrden ?? null}
          cotizacion={cotizacion}
          guardando={entregar.isPending}
          onCerrar={() => setEntregando(false)}
          onConfirmar={confirmarEntrega}
        />
      ) : (
        <DialogEntregar
          orden={entregando ? orden : null}
          cuentas={cuentas}
          cotizacion={cotizacion}
          guardando={entregar.isPending}
          saldoAFavor={fichaCliente ? aNumero(fichaCliente.saldo_a_favor ?? 0) : null}
          clienteAnclaje={fichaCliente ? monedaAnclaje(fichaCliente.cliente) : null}
          onCerrar={() => setEntregando(false)}
          onConfirmar={confirmarEntrega}
        />
      )}

      <DialogEstado
        orden={orden}
        destino={destino}
        onCerrar={() => setDestino(null)}
        guardando={cambiarEstado.isPending}
        cotizacion={cotizacion}
        onConfirmar={(datos) => {
          if (!orden || !destino) return;
          const nuevo = destino;
          cambiarEstado.mutate(
            { id: orden.id, estado: nuevo, datos },
            {
              onSuccess: () => {
                setDestino(null);
                mostrarExito(
                  `${nombreOrden(orden)} · ${ETIQUETA_ESTADO[nuevo]}`,
                  efectoDe(nuevo, orden),
                );
              },
              onError: mostrarError,
            },
          );
        }}
      />
    </Pagina>
  );
}

/**
 * El filtro por etapa: tres chips y "todas", con la cuenta al lado.
 *
 * Reemplaza al tablero de nueve columnas. Para siete equipos en el taller, lo
 * que importa no es en cual de nueve estados esta cada uno: es a cuales les
 * falta el numero del tecnico y cuales ya se pueden entregar.
 */
type EtapaOTodas = Etapa | "TODAS";

function FiltroEtapas({
  etapa,
  onEtapa,
  cantidades,
  total,
}: {
  etapa: Etapa | null;
  onEtapa: (e: Etapa | null) => void;
  cantidades: Record<Etapa, number>;
  total: number;
}): JSX.Element {
  const opciones: Array<OpcionSegmented<EtapaOTodas>> = [
    { valor: "TODAS", etiqueta: "Todas", contador: total },
    ...ETAPAS.map((e) => ({ valor: e.clave, etiqueta: e.titulo, contador: cantidades[e.clave] })),
  ];
  return (
    <Segmented
      apariencia="chips"
      // `md` (h-11 / sm:h-10) es el alto del buscador que tienen al lado: con
      // `sm` los chips quedaban 8px mas bajos y la fila no apoyaba en ninguna
      // linea.
      tamano="md"
      etiqueta="Filtrar por etapa"
      opciones={opciones}
      valor={etapa ?? "TODAS"}
      onChange={(v) => onEtapa(v === "TODAS" ? null : v)}
      // `min-w-0` y no `shrink-0`: con el buscador a su ancho minimo, los chips
      // envuelven en vez de empujar la fila fuera de la pantalla.
      className="min-w-0"
    />
  );
}
